import React, { useMemo, useState } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import {
  ShowChart,
  BatteryChargingFull,
  Power,
  ElectricBolt,
  Assessment,
  FlashOn,
  OfflineBolt
} from '@mui/icons-material';
import { format } from 'date-fns';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      style={{ maxWidth: '100%' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const summaryTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    px: { xs: 1, sm: 2 },
    py: { xs: 0.75, sm: 1 },
    verticalAlign: 'top',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    fontSize: { xs: '0.72rem', sm: '0.875rem' },
  },
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>) => {
  const translate = useTranslate();
  if (!active || !payload || !payload.length) return null;

  return (
    <Box sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid #ccc',
      borderRadius: 1,
      padding: 1.5,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <Typography variant="caption" fontWeight="bold" gutterBottom>
        {translate('resources.charging-sessions.show.time')}: {label}
      </Typography>
      {payload.map((entry: any, index: number) => (
        <Typography
          key={index}
          variant="caption"
          sx={{ color: entry.color, display: 'block' }}
        >
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value} {entry.unit || ''}
        </Typography>
      ))}
    </Box>
  );
};

// Process charging curve data
const processChargingCurve = (chargingCurve: any[]) => {
  if (!chargingCurve || chargingCurve.length === 0) return [];

  return chargingCurve.map((point: any) => ({
    time: `${Math.floor(point.t / 60)}:${(point.t % 60).toString().padStart(2, '0')}`,
    timeSeconds: point.t,
    power: parseFloat(point.p?.toFixed(2) || '0')
  }));
};

// Process meter values samples
const processMeterSamples = (samples: any[]) => {
  if (!samples || samples.length === 0) return [];

  return samples.map((sample: any) => ({
    time: sample.t,
    power: parseFloat(sample.p?.toFixed(2) || '0'),
    voltage: parseFloat(sample.v?.toFixed(1) || '0'),
    current: parseFloat(sample.a?.toFixed(2) || '0'),
    energy: parseFloat((sample.wh / 1000)?.toFixed(3) || '0'), // Convert Wh to kWh
    soc: sample.soc,
    temperature: sample.temp
  }));
};

// Meter Values Summary Stats
const MeterValuesSummary = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.meter_values_compressed) return null;

  const { first, last } = record.meter_values_compressed;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'info.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <BatteryChargingFull sx={{ color: 'info.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {translate('resources.charging-sessions.show.meterReadingsSummary')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom>
              {translate('resources.charging-sessions.show.startOfCharging')}
            </Typography>
            <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={summaryTableSx}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: { xs: '45%', sm: '40%' } }}><strong>{translate('resources.charging-sessions.show.power')}</strong></TableCell>
                  <TableCell>{first?.power_kw?.toFixed(2) || '-'} kW</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.voltage')}</strong></TableCell>
                  <TableCell>{first?.voltage_v?.toFixed(1) || '-'} V</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.current')}</strong></TableCell>
                  <TableCell>{first?.current_a?.toFixed(2) || '-'} A</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.meter')}</strong></TableCell>
                  <TableCell>{first?.value_wh?.toFixed(0) || '0'} Wh</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.time')}</strong></TableCell>
                  <TableCell>{first?.timestamp ? format(new Date(first.timestamp), 'HH:mm:ss') : '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </TableContainer>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom>
              {translate('resources.charging-sessions.show.endOfCharging')}
            </Typography>
            <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={summaryTableSx}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: { xs: '45%', sm: '40%' } }}><strong>{translate('resources.charging-sessions.show.power')}</strong></TableCell>
                  <TableCell>{last?.power_kw?.toFixed(2) || '-'} kW</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.voltage')}</strong></TableCell>
                  <TableCell>{last?.voltage_v?.toFixed(1) || '-'} V</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.current')}</strong></TableCell>
                  <TableCell>{last?.current_a?.toFixed(2) || '-'} A</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.meter')}</strong></TableCell>
                  <TableCell>{last?.value_wh?.toFixed(0) || '0'} Wh</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>{translate('resources.charging-sessions.show.time')}</strong></TableCell>
                  <TableCell>{last?.timestamp ? format(new Date(last.timestamp), 'HH:mm:ss') : '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
        </Box>

        {record.meter_values_compressed.total_readings && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {translate('resources.charging-sessions.show.totalReadingsCaptured')}: {record.meter_values_compressed.total_readings} {translate('resources.charging-sessions.show.samples')}
            {record.meter_values_compressed.samples && (
              <> ({translate('resources.charging-sessions.show.showingKeyDataPoints')} {record.meter_values_compressed.samples.length} {translate('resources.charging-sessions.show.keyDataPoints')})</>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Tabbed Charts Component
const TabbedCharts = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const chartHeight = isMobile ? 260 : 350;

  const chargingCurveData = useMemo(() => {
    if (!record?.charging_curve_summary) return [];
    return processChargingCurve(record.charging_curve_summary);
  }, [record]);

  const meterSamplesData = useMemo(() => {
    if (!record?.meter_values_compressed?.samples) return [];
    return processMeterSamples(record.meter_values_compressed.samples);
  }, [record]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!chargingCurveData || chargingCurveData.length === 0) {
    return (
      <Alert severity="info">{translate('resources.charging-sessions.show.noChargingCurveData')}</Alert>
    );
  }

  const hasSampleData = meterSamplesData && meterSamplesData.length > 0;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Assessment sx={{ color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="600">{translate('resources.charging-sessions.show.chargingAnalytics')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('resources.charging-sessions.show.detailedMetricsPerformance')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="charging charts tabs"
            variant={isMobile ? "fullWidth" : "scrollable"}
            scrollButtons={isMobile ? false : "auto"}
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTab-root': {
                minWidth: { xs: 0, sm: 90 },
                minHeight: { xs: 40, sm: 48 },
                px: { xs: 0.5, sm: 2 },
                py: { xs: 0.75, sm: 1.5 },
                fontSize: { xs: '0.62rem', sm: '0.875rem' },
                lineHeight: 1.1,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textTransform: 'none',
              },
            }}
          >
            <Tab label={translate('resources.charging-sessions.show.power')} icon={isMobile ? undefined : <ShowChart />} iconPosition="start" />
            {hasSampleData && <Tab label={translate('resources.charging-sessions.show.voltage')} icon={isMobile ? undefined : <FlashOn />} iconPosition="start" />}
            {hasSampleData && <Tab label={translate('resources.charging-sessions.show.current')} icon={isMobile ? undefined : <OfflineBolt />} iconPosition="start" />}
            {hasSampleData && <Tab label={translate('resources.charging-sessions.show.energy')} icon={isMobile ? undefined : <ElectricBolt />} iconPosition="start" />}
            {hasSampleData && <Tab label={translate('resources.charging-sessions.show.allMetrics')} icon={isMobile ? undefined : <Assessment />} iconPosition="start" />}
          </Tabs>
        </Box>

        {/* Power Tab */}
        <TabPanel value={tabValue} index={0}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={chargingCurveData}>
              <defs>
                <linearGradient id="colorPower" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00C853" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#00C853" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: isMobile ? 10 : 12 }}
                minTickGap={isMobile ? 24 : 8}
                label={isMobile ? undefined : { value: 'Time (mm:ss)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 10 : 12 }}
                width={isMobile ? 36 : 60}
                label={isMobile ? undefined : { value: 'Power (kW)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="power"
                stroke="#00C853"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorPower)"
                name="Power"
                unit="kW"
              />
            </AreaChart>
          </ResponsiveContainer>
        </TabPanel>

        {/* Voltage Tab */}
        {hasSampleData && (
          <TabPanel value={tabValue} index={1}>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={meterSamplesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  minTickGap={isMobile ? 24 : 8}
                  label={isMobile ? undefined : { value: 'Time', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 36 : 60}
                  label={isMobile ? undefined : { value: 'Voltage (V)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                {!isMobile && <Legend />}
                <Line
                  type="monotone"
                  dataKey="voltage"
                  stroke="#FF9800"
                  strokeWidth={2}
                  dot={false}
                  name="Voltage"
                  unit="V"
                />
              </LineChart>
            </ResponsiveContainer>
          </TabPanel>
        )}

        {/* Current Tab */}
        {hasSampleData && (
          <TabPanel value={tabValue} index={2}>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={meterSamplesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  minTickGap={isMobile ? 24 : 8}
                  label={isMobile ? undefined : { value: 'Time', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 36 : 60}
                  label={isMobile ? undefined : { value: 'Current (A)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                {!isMobile && <Legend />}
                <Line
                  type="monotone"
                  dataKey="current"
                  stroke="#2196F3"
                  strokeWidth={2}
                  dot={false}
                  name="Current"
                  unit="A"
                />
              </LineChart>
            </ResponsiveContainer>
          </TabPanel>
        )}

        {/* Energy Delivered Tab */}
        {hasSampleData && (
          <TabPanel value={tabValue} index={3}>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <AreaChart data={meterSamplesData}>
                <defs>
                  <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976D2" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1976D2" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  minTickGap={isMobile ? 24 : 8}
                  label={isMobile ? undefined : { value: 'Time', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 36 : 60}
                  label={isMobile ? undefined : { value: 'Energy (kWh)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="energy"
                  stroke="#1976D2"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorEnergy)"
                  name="Energy"
                  unit="kWh"
                />
              </AreaChart>
            </ResponsiveContainer>
          </TabPanel>
        )}

        {/* All Metrics Tab - Combined View */}
        {hasSampleData && (
          <TabPanel value={tabValue} index={4}>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <ComposedChart data={meterSamplesData}>
                <defs>
                  <linearGradient id="colorPowerAll" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00C853" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#00C853" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  minTickGap={isMobile ? 24 : 8}
                  label={isMobile ? undefined : { value: 'Time', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 36 : 60}
                  label={isMobile ? undefined : { value: 'Power (kW) / Energy (kWh)', angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 36 : 60}
                  label={isMobile ? undefined : { value: 'Voltage (V) / Current (A)', angle: 90, position: 'insideRight' }}
                />
                <Tooltip content={<CustomTooltip />} />
                {!isMobile && <Legend />}
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="power"
                  stroke="#00C853"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPowerAll)"
                  name="Power"
                  unit="kW"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="energy"
                  stroke="#1976D2"
                  strokeWidth={2}
                  dot={false}
                  name="Energy"
                  unit="kWh"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="voltage"
                  stroke="#FF9800"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="5 5"
                  name="Voltage"
                  unit="V"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="current"
                  stroke="#9C27B0"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="5 5"
                  name="Current"
                  unit="A"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </TabPanel>
        )}
      </CardContent>
    </Card>
  );
};

// Main Meter Values Charts Component
export const MeterValuesCharts = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <MeterValuesSummary />
      <TabbedCharts />
    </Box>
  );
};

export default MeterValuesCharts;
